import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import { useState, useRef, type FormEvent } from 'react';
import { stats } from '@/mocks/faqs';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u541t3pcjqs2fcsmb0', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          return;
        }
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 种子搜索',
    description: '种子搜索致力于为用户提供最高效的BT资源检索服务，了解更多关于我们的品牌故事、团队理念与联系方式。',
    url: 'https://gdjsfzyxgs.cn/about',
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://gdjsfzyxgs.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-100 text-primary-700',
    accent: 'bg-accent-100 text-accent-700',
    secondary: 'bg-secondary-100 text-secondary-700',
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 种子搜索 | 品牌介绍与联系方式"
        description="种子搜索致力于为用户提供最高效的BT资源检索服务。了解我们的品牌故事、团队理念、用户口碑与联系方式。"
        keywords="种子搜索,关于种子搜索,种子搜索介绍,BT搜索工具,联系我们"
        canonicalUrl="https://gdjsfzyxgs.cn/about"
        schemaJson={{ ...pageSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

          <div className="mb-14">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-3">
              关于种子搜索
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed max-w-[700px]">
              致力于打造最高效、最全面的BT资源检索平台，为全球中文用户提供优质的磁力链接与种子文件搜索服务
            </p>
          </div>

          <section className="mb-14 md:mb-20">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  种子搜索诞生于互联网资源共享的时代浪潮之中。随着BT技术的发展和DHT网络的成熟，越来越多的用户需要一个高效、精准的资源检索工具。我们的创始团队深耕P2P技术领域多年，深刻理解用户在寻找优质资源时面临的挑战——信息分散、结果不准确、链接失效等问题长期困扰着广大用户。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  我们汇集了分布式爬虫、自然语言处理和搜索引擎等前沿技术，构建了一个覆盖海量DHT节点的索引系统。经过多年的技术积累和持续优化，种子搜索已经发展成为中文互联网最受欢迎的BT资源搜索工具之一，月均服务数百万用户，累计处理数十亿次搜索请求。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们的使命是让每一个用户都能在信息海洋中快速找到自己需要的资源，让资源共享变得更加简单、高效。无论你是在寻找最新上映的电影，还是珍藏版的无损音乐，亦或是实用的开源软件，种子搜索都将成为你最可靠的资源发现伙伴。
                </p>
              </div>
              <div className="flex-1 w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20digital%20technology%20office%20workspace%20with%20warm%20lighting%2C%20clean%20minimalist%20aesthetic%2C%20abstract%20data%20visualization%20on%20screen%2C%20professional%20yet%20creative%20atmosphere%2C%20warm%20amber%20and%20terracotta%20accents%2C%20soft%20natural%20light%2C%20no%20human%20faces&width=1000&height=700&seq=about-brand&orientation=landscape"
                  alt="种子搜索品牌故事"
                  className="w-full h-auto rounded-xl"
                />
              </div>
            </div>
          </section>

          <section className="mb-14 md:mb-20">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6 text-center">
              平台数据
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-6 text-center">
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${colorMap[stat.color]}`}>
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14 md:mb-20">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  name: '电影爱好者小张',
                  role: '资深用户 3年',
                  text: '种子搜索是我用过最高效的BT资源检索工具，搜索结果精准，界面简洁易用。无论是找新片还是老片，几乎都能在这里找到，强烈推荐！',
                  rating: 5,
                },
                {
                  name: '音乐发烧友阿琳',
                  role: '活跃用户 2年',
                  text: '作为一个无损音乐爱好者，种子搜索帮我找到了很多珍贵的专辑资源。搜索速度快，磁力链接更新及时，真的是宝藏网站。',
                  rating: 5,
                },
                {
                  name: '程序员老李',
                  role: '忠实用户 4年',
                  text: '软件资源板块做得特别好，开源工具、开发环境都能快速搜到。网站的搜索算法很智能，排序结果也很合理，大大节省了查找时间。',
                  rating: 5,
                },
              ].map((review, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                    <div className="text-xs text-foreground-400">{review.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  如果你在使用过程中遇到任何问题，或有合作意向，欢迎通过以下方式联系我们。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮件</div>
                      <div className="text-sm text-foreground-500">support@seedsearch.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">官方网站</div>
                      <div className="text-sm text-foreground-500">gdjsfzyxgs.cn</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-service-line text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">工作时间</div>
                      <div className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">
                  发送消息
                </h3>
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-readdy-form=""
                >
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      name="name"
                      className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-200 bg-white outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-200 bg-white outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">消息</label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-200 bg-white outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                      placeholder="请输入你的消息（不超过500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    className="w-full py-2.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    发送消息
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 bg-accent-50 rounded-lg p-3 text-center font-medium">
                      消息已成功发送，我们会尽快回复你！
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3 text-center">
                      {formError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </section>

          <section className="mt-14 md:mt-20">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
              免责声明
            </h2>
            <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70">
              <p className="text-sm text-foreground-600 leading-relaxed">
                种子搜索仅提供资源搜索技术服务，不存储、不缓存任何文件内容，也不对搜索结果中的文件内容负责。本网站基于DHT网络公开信息进行索引，所有搜索结果均来自互联网公开数据。我们坚决维护知识产权，如果权利人认为搜索结果侵犯了其合法权益，请通过我们的联系邮箱发送权利通知，我们将在收到通知后及时处理。请用户遵守当地法律法规，仅下载和使用版权允许的内容。使用本网站即表示你已阅读并同意本免责声明。
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}